import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { SlideOver, FormField, AutosaveStatus, FormErrorBanner, StatusPicker, DatePicker, TimePicker, AddressInput, Button, IconButton, inputCls, ChoiceKeys, Combobox, TripStrip, MoveSwitcher, MoveHeaderCard, PopoverPanel } from '../ui';
// Moves (2026-09-18, moves-architecture.md): dates / addresses / job type
// live on the client's MOVE. The file shows one move at a time (the
// switcher picks it) and every date/address edit here writes that move.
import { movesForClient, currentMoveFor, optionsForMove, moveDatesSummary, jobTypeShort, moveFieldsFromClientShape, clientFieldsFromMove, MOVE_DATE_FIELDS, legsForMove } from '../../lib/moves';
import { IconEdit, IconPhone, IconClipboard, IconSettings, IconTruck, IconChevronLeft, IconChevronRight } from '../../components/icons';
import { statusTint } from '../../lib/theme';
import { useStore, useNav } from '../../store/StoreContext';
import { useAuth } from '../../auth/AuthContext';
import MoveDetailsEditor from '../forms/MoveDetailsEditor';
import ClientPricingTab from '../forms/ClientPricingTab';
import ClientNotesPanel from '../forms/ClientNotesPanel';
import ClientEstimatesTab from '../forms/ClientEstimatesTab';
import PhotoUpload from '../PhotoUpload';
import PhotoGallery from '../PhotoGallery';
import ActivityLog from '../ActivityLog';
import { LEAD_SOURCES, JOB_TYPES, PACKING_OPTIONS, PACKING_LABELS, CREW_ROLES, QUICK_DISPATCH_TYPES } from '../../lib/constants';
import QuickDispatchModal from './QuickDispatchModal';
import { getClientStatuses } from '../../utils/';
import db from '../../lib/database';
import { formatPhone, maskPhoneInput, phonesOf } from '../../lib/phone';
import { useToast } from '../../hooks/useToast';
import { useAutosave } from '../../hooks/useAutosave';
import ConfirmDialog from '../ui/ConfirmDialog';
import PhonesEditor, { normalizePhonesForSave } from '../forms/PhonesEditor';
import { findLikelyDuplicates } from '../../lib/clientDupes';
import { tenancyPlace, byId, paidThruStatus } from '../../lib/storageHelpers';

// inputCls centralized in components/ui/formStyles.js (Phase 8). All
// existing className={inputCls + ' ...'} call sites in this file work
// unchanged via the named import above.

export default function ClientIntakeModal({ client, onClose, onSave, initialViewMode, jobs: modalJobs, crew: modalCrew, trucks: modalTrucks, initialMoveDate = null, neighbors = null, onSwitch = null }) {
    const { config, estimates, saveEstimate, crew: allCrew, saveJob, removeJob, saveCrewNoteSet, saveInternalNoteSet, clients: allClients, storageUnits, storageFacilities, storageTenancies, moves, saveMove, createMove, setCurrentMove, removeMove, deleteClient } = useStore();
    const { navigateToClient, setCurrentPage } = useNav();
    const { show } = useToast();
    const { can: canCap } = useAuth();
    // Merge tool (dupes cleanup) — destructive, super-admin tier.
    const canMerge = canCap ? canCap('delete_records') : false;
    const [mergeOpen, setMergeOpen] = useState(false);
    const [mergeSearch, setMergeSearch] = useState('');
    const [mergeTarget, setMergeTarget] = useState(null);
    const [merging, setMerging] = useState(false);
    const doMerge = async () => {
        if (!mergeTarget || !client?.id) return;
        setMerging(true);
        try {
            await db.mergeClients(client.id, mergeTarget.id);
            show(`Merged into ${mergeTarget.name} — estimates, jobs, photos and notes moved.`, 'success');
            onClose();
            navigateToClient(mergeTarget.id);
        } catch (e) {
            show(`Merge failed: ${e.message}`, 'error');
        } finally {
            setMerging(false);
            setMergeTarget(null);
            setMergeOpen(false);
        }
    };
    const clientStatuses = getClientStatuses(config);
    const [activeTab, setActiveTab] = useState('details');
    const isExisting = !!client?.id;
    // Options created from the New Estimate tab (db.createOption) reach the
    // store through realtime / refreshMove; until they do, keep them here so
    // the Estimates tab shows the row the instant it's created (2026-09-18).
    const [pendingCreated, setPendingCreated] = useState([]);
    const clientEstimates = isExisting
        ? [...estimates.filter(e => e.client_id === client.id), ...pendingCreated.filter(p => p.client_id === client.id && !estimates.some(e => e.id === p.id))]
        : [];
    // The client row the page handed us is a snapshot; current_move_id
    // changes under us (switcher, booking flips it), so read the live row.
    const liveClient = (isExisting && (allClients || []).find(c => c.id === client.id)) || client;
    const clientMoves = isExisting ? movesForClient(moves, client.id) : [];
    const current = isExisting ? currentMoveFor(liveClient, moves) : null;
    const currentOptions = current ? optionsForMove(clientEstimates, current.id) : [];
    // Owners who can do walkthroughs (brothers — expandable later via roles/settings)
    const walkthroughAssignees = (allCrew || []).filter(c => c.role === 'owner').filter(c => !c.deactivated);
    const defaults = {
        name: '', phone: '', phones: [], email: '',
        lead_source: 'manual', status: 'new_lead', entry_type: 'phone',
        origin_address: '', destination_address: '', additional_stops: '',
        move_date: null, move_time: '', date_tentative: false, walkthrough_date: null, walkthrough_time: '', walkthrough_assigned_to: '', virtual_walkthrough: false,
        job_type: 'LOCAL', packing_needed: 'tbd', pack_date: null, pack_time: '', heavy_items: '',
        secondary_name: '', secondary_phone: '', secondary_email: '',
        from_sqft: 0, from_levels: 1, from_stairs: false, from_stairs_steps: null, from_elevator: false, from_apartment: false, from_apartment_floor: '', from_driveway: '', from_walk_distance: '',
        to_sqft: 0, to_levels: 1, to_stairs: false, to_stairs_steps: null, to_elevator: false, to_apartment: false, to_apartment_floor: '', to_driveway: '', to_walk_distance: '',
        referral_name: '', sms_approved: false, notes: '', description: '', internal_notes: '',
        crew_size_needed: 0, trucks_needed: ''
    };
    // initialMoveDate: Capacity page "Book this day" pre-aims a NEW intake
    // at the clicked date (never applied to existing clients).
    // Existing client: the move fields on the client row are a MIRROR that
    // can lag the current move (it only refreshes when the move is written),
    // so seed the form from the move itself at mount (2026-09-18).
    const [form, setForm] = useState(() => {
        if (!client) return { ...defaults, move_date: initialMoveDate || null };
        const seed = { ...defaults, ...client };
        if (current) {
            const stopFlat = (current.stop_details && typeof current.stop_details === 'object') ? current.stop_details : {};
            return { ...seed, ...stopFlat, ...clientFieldsFromMove(current) };
        }
        return seed;
    });
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(!initialViewMode);
    const [dirty, setDirty] = useState(false);
    const readOnly = !editing;
    // Autosave (feedback.md 5C): existing clients save ~1.2s after the last
    // change while in edit mode; the footer status line reports it. New
    // intakes keep the explicit Save Client button. `autosaveRef` is filled
    // once the hook exists (it needs handleSave, defined below).
    const autosaveRef = useRef(null);
    const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); if (client?.id) autosaveRef.current?.touch(); };

    // ── New-intake move dates (2026-09-18, trip strip pick 1B) ──────────
    // A new client has no move row until Save, so the TripStrip edits this
    // draft; handleSave maps it onto the client insert (load → move_date /
    // move_time / date_tentative, pack_date / pack_time — the DB trigger
    // seeds the lead move from those) and writes the fields the client row
    // can't carry (unload, pack_tentative) to the new move right after.
    // Existing clients don't use it: their MoveHeaderCard writes the move.
    const [draft, setDraft] = useState({
        load_date: initialMoveDate || null, load_time: null, load_tentative: false,
        unload_date: null, unload_window_end: null, unload_time: null, unload_tentative: false,
        pack_date: null, pack_time: null, pack_tentative: false,
    });
    // Packing day — when partial/full packing is chosen on a NEW intake,
    // seed the pack day to load − 1 and keep it tracking the load date
    // until the operator picks one on the strip. Existing clients: the
    // move owns its pack date (the strip's "+ Pack day" adds one).
    const [packDateTouched, setPackDateTouched] = useState(false);
    const patchDraft = (patch) => { setDraft(d => ({ ...d, ...patch })); setDirty(true); if ('pack_date' in patch) setPackDateTouched(true); };
    const shiftDateStr = (iso, days) => {
        if (!iso) return null;
        const [y, m, d] = String(iso).split('-').map(Number);
        if (!y) return null;
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + days);
        return dt.toISOString().slice(0, 10);
    };
    const wantsPacking = form.packing_needed === 'partial' || form.packing_needed === 'full';
    useEffect(() => {
        if (client?.id || !wantsPacking || packDateTouched || !draft.load_date) return;
        const seed = shiftDateStr(draft.load_date, -1);
        if (seed && seed !== draft.pack_date) setDraft(d => ({ ...d, pack_date: seed }));
    }, [wantsPacking, draft.load_date, packDateTouched]);

    // Move switcher (pick 3B): when the file's current move changes (chip
    // tap, "+ New move", or a booking flipping it), re-seed the form's
    // move-mirror fields (addresses, access, type, walkthrough) from that
    // move so the editors below show it. Skipped on first render — the
    // form was seeded from the client row, which mirrors the current move.
    const prevMoveIdRef = useRef(current?.id || null);
    // The route editor (MoveDetailsEditor) seeds its origin / stops /
    // destination state once on mount, so it's keyed by `editorSeed` —
    // bumped only AFTER the form holds the new move's fields (the key and
    // the re-seed must not land in the same render, or the editor would
    // mount on the old addresses and write them back to the new move).
    const [editorSeed, setEditorSeed] = useState(current?.id || 'new');
    useEffect(() => {
        if (!client?.id) return;
        const id = current?.id || null;
        if (id === prevMoveIdRef.current) return;
        prevMoveIdRef.current = id;
        // stop_details jsonb → the flat stop_N_* keys the route editor reads.
        const stopFlat = (current?.stop_details && typeof current.stop_details === 'object') ? current.stop_details : {};
        if (current) { setForm(f => ({ ...f, ...stopFlat, ...clientFieldsFromMove(current) })); setEditorSeed(id); }
    }, [current?.id]);
    const selectMove = async (m) => {
        if (!m || m.id === current?.id) return;
        // Don't let a pending autosave land identity edits on the wrong move.
        if (autosaveRef.current?.pending()) { const ok = await autosaveRef.current.flush(); if (!ok) { show('Finish the edit that couldn\'t save before switching moves', 'error'); return; } }
        try { await setCurrentMove(client.id, m.id); }
        catch (e) { show('Couldn\'t switch move: ' + e.message, 'error'); }
    };
    // "+ New move" menu: blank, or (when the current move looks like it went
    // into storage / is done) "Deliver out of storage" chained to it (§2a).
    const [newMoveOpen, setNewMoveOpen] = useState(false);
    const newMoveRef = useRef(null);
    const offerStorageOut = !!current && (
        !!current.storage_mode
        || /storage|self.?storage|unit/i.test(current.destination_address || '')
        || ['completed', 'booked'].includes(current.status)
    );
    const newMove = async (kind) => {
        setNewMoveOpen(false);
        if (!client?.id) return;
        if (autosaveRef.current?.pending()) { const ok = await autosaveRef.current.flush(); if (!ok) { show('Finish the edit that couldn\'t save before adding a move', 'error'); return; } }
        try {
            await createMove(client.id, {}, kind === 'storage' ? { fromMove: current } : {});
            show(kind === 'storage' ? 'Delivery out of storage added — pick its dates' : 'New move added — pick its dates');
            setActiveTab('details');
            setEditing(true);
        } catch (e) { show('Couldn\'t add a move: ' + e.message, 'error'); }
    };

    // Removing a move (Jaylen 2026-09-18: "+ New move" had no way back).
    // Only a BARE move can go — no quote, no leg on the board — and only
    // while the client keeps another move. `moveIsBare` also feeds the
    // auto-discard on Done / close: a move that was added and never given
    // a date or an address is dropped silently instead of lingering as an
    // empty chip.
    const moveIsBare = (m) => !!m && optionsForMove(estimates, m.id).length === 0 && legsForMove(modalJobs || [], m.id).length === 0;
    const moveIsUntouched = (m) => moveIsBare(m) && !m.load_date && !m.unload_date && !m.pack_date && !(m.origin_address || '').trim() && !(m.destination_address || '').trim() && !m.walkthrough_date;
    const canRemoveMove = !!current && moveIsBare(current) && clientMoves.length > 1;
    const [removeMoveConfirm, setRemoveMoveConfirm] = useState(false);
    const doRemoveMove = async () => {
        setRemoveMoveConfirm(false);
        if (!current) return;
        // Don't let a pending autosave resurrect it as an upsert.
        if (autosaveRef.current?.pending()) { const ok = await autosaveRef.current.flush(); if (!ok) { show('Finish the edit that couldn\'t save first', 'error'); return; } }
        try { await removeMove(current.id); show('Move removed'); }
        catch (e) { show('Couldn\'t remove the move: ' + e.message, 'error'); }
    };
    // Delete the whole client (super-admin tier, see db.deleteClient).
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const doDeleteClient = async () => {
        if (!client?.id) return;
        setDeleting(true);
        try {
            // A pending autosave would re-insert the row after the delete.
            if (autosaveRef.current?.pending()) await autosaveRef.current.flush();
            await deleteClient(client.id);
            setDeleteConfirm(false);
            show(`${client.name || 'Client'} deleted`);
            discardRef.current = true;
            onClose();
        } catch (e) {
            show('Couldn\'t delete: ' + e.message, 'error');
        } finally { setDeleting(false); }
    };
    // Auto-discard: an added-but-untouched extra move goes when the file is
    // closed or locked (Done). Best-effort, silent.
    const discardUntouchedMove = async () => {
        if (!client?.id || !current || clientMoves.length < 2 || !moveIsUntouched(current)) return;
        try { await removeMove(current.id); } catch { /* leave it */ }
    };

    // New-client intake: notes can't be saved as rows until the client exists.
    // Capture one office + one crew note here and create them in handleSave once
    // we have the new client id (no save-then-reopen). EXISTING clients use the
    // live <ClientNotesPanel> instead — full add/edit/delete, no edit-mode gate.
    const [pendingCrewNote, setPendingCrewNote] = useState({ label: '', body: '' });
    const [pendingInternalNote, setPendingInternalNote] = useState({ label: '', body: '' });
    // In-progress note draft from <ClientNotesPanel>, lifted here so it survives
    // tab switches / panel unmounts and feeds the unsaved-changes close guard
    // (was silently lost when the modal got dismissed mid-compose).
    const [noteDraft, setNoteDraft] = useState(null);
    const noteDraftDirty = !!(noteDraft && ((noteDraft.body || '').trim() || (noteDraft.label || '').trim()));
    // Secondary contact is low-frequency — collapsed behind a reveal to cut
    // clutter, auto-expanded when the client already has secondary data.
    const [showSecondary, setShowSecondary] = useState(
        !!(client?.secondary_name || client?.secondary_phone || client?.secondary_email)
    );
    // Auto-collapse the left-column sections for an EXISTING client (data is
    // already filled) so Notes sits near the top; new-client intake opens them
    // expanded for data entry. Each is one click to expand; validation errors
    // force the relevant section open so required fields can't hide.
    const [openSections, setOpenSections] = useState(() => {
        const isNew = !client?.id;
        // Contact stays expanded by default (name/phone are the at-a-glance
        // essentials); Job & Service + Admin auto-collapse on a filled client.
        return { contact: true, job: isNew, admin: isNew };
    });
    const toggleSection = (k) => setOpenSections(s => ({ ...s, [k]: !s[k] }));
    // Scheduling a walkthrough is a discrete action that should be
    // available even when the client modal is in view mode (it's the
    // most common "I just need to do one thing on this client" flow).
    // This setter wraps the regular `set()` and silently flips us into
    // edit mode if we weren't already — that surfaces the Save button
    // at the bottom of the modal so the new walkthrough actually
    // persists. Fix for "Schedule Walkthrough button disappeared" UX
    // gap: the panel had been hidden under !readOnly.
    const setWalkthrough = (k, v) => {
        if (!editing) setEditing(true);
        set(k, v);
    };
    // Unsaved-changes guard — in-app ConfirmDialog (was window.confirm,
    // off-brand + blocks the page invisibly; live test 2026-07-06).
    const [closeConfirm, setCloseConfirm] = useState(false);
    // SlideOver hands us its animated close via closeRef; discardRef lets
    // the confirm's Discard button pass the guard once. pendingSwitchRef
    // holds a neighbour client the operator tried to flip to mid-edit —
    // Discard then switches instead of closing.
    const closeRef = useRef(null);
    const discardRef = useRef(false);
    const pendingSwitchRef = useRef(null);
    const requestClose = () => (closeRef.current ? closeRef.current() : onClose());
    // Leave guard (feedback.md 5C). Existing client in edit mode: flush a
    // pending autosave first; only an UNSAVEABLE edit asks. New intake /
    // note draft: the classic "unsaved changes" ask.
    const pendingLeaveRef = useRef(null);
    const needsAsk = () => (!client?.id && dirty) || noteDraftDirty;
    const leave = async (proceed) => {
        if (client?.id && autosaveRef.current?.pending()) {
            const ok = await autosaveRef.current.flush();
            if (!ok) { pendingLeaveRef.current = proceed; setCloseConfirm(true); return; }
        }
        if (needsAsk()) { pendingLeaveRef.current = proceed; setCloseConfirm(true); return; }
        proceed();
    };
    // Closing drops an added-but-untouched extra move (fire and forget —
    // the panel is already leaving).
    const closeNow = () => { discardRef.current = true; discardUntouchedMove(); requestClose(); };
    const handleClose = () => { leave(closeNow); };
    // Veto guard: ESC/scrim ask here BEFORE the exit animation — return
    // false keeps the panel fully open under our confirm dialog.
    const beforeClose = () => {
        if (discardRef.current) return true;
        if (!(client?.id && autosaveRef.current?.pending()) && !needsAsk()) return true;
        leave(closeNow);
        return false;
    };
    const discardAndClose = () => {
        setCloseConfirm(false);
        const proceed = pendingLeaveRef.current;
        pendingLeaveRef.current = null;
        const target = pendingSwitchRef.current;
        pendingSwitchRef.current = null;
        if (proceed) { proceed(); return; }
        if (target && onSwitch) { onSwitch(target); return; }
        closeNow();
    };
    // Header ‹ › — walk the list without closing the file.
    const switchTo = (target) => {
        if (!target || !onSwitch) return;
        leave(() => onSwitch(target));
    };

    const [photos, setPhotos] = useState([]);
    useEffect(() => { if (client?.id) db.getPhotos(client.id).then(p => setPhotos(p)).catch(() => {}); }, [client?.id]);
    const handlePhotoUploaded = (photo) => setPhotos(prev => [photo, ...prev]);
    const handlePhotoDelete = async (photo) => { await db.deletePhoto(photo); setPhotos(prev => prev.filter(p => p.id !== photo.id)); };
    // Manual reorder (sql/089, 2026-09-11): the gallery hands back every
    // photo in display order; renumber 1..n locally and in the DB.
    const handlePhotoReorder = async (ordered) => {
        try {
            await db.reorderPhotos(ordered.map(p => p.id));
            setPhotos(ordered.map((p, i) => ({ ...p, sort_order: i + 1 })));
            show('Photo order saved');
        } catch (e) { show('Couldn\'t save photo order: ' + e.message, 'error'); }
    };

    const clientJobs = (modalJobs || []).filter(j => j.client_id === client?.id);

    // Hydrate walkthrough time/assignee from existing walkthrough job
    useEffect(() => {
        if (!client?.id) return;
        const wtJob = (modalJobs || []).find(j => j.client_id === client.id && j.type === 'WALKTHROUGH');
        if (wtJob) {
            setForm(f => ({
                ...f,
                walkthrough_time: f.walkthrough_time || wtJob.time || '',
                walkthrough_assigned_to: f.walkthrough_assigned_to || wtJob.leader_id || '',
            }));
        }
    }, [client?.id, modalJobs]);

    const [errors, setErrors] = useState({});
    const validate = () => {
        // Short messages only (feedback.md 4B, Jaylen: "just 'field required'").
        const errs = {};
        if (!form.name?.trim()) errs.name = 'Required';
        // Multi-phone: valid = at least one row with a real number.
        const phoneRows = normalizePhonesForSave(form.phones?.length ? form.phones : phonesOf(form));
        if (!phoneRows.length) errs.phone = 'Required';
        if (!form.origin_address?.trim()) errs.origin_address = 'Required';
        // Time is REQUIRED with a scheduled walkthrough (Jaylen 2026-06-09)
        // — the lead pipeline auto-promotes to "Awaiting Brothers' Review"
        // 30 minutes after the scheduled date+time, so a time-less
        // walkthrough has no anchor for that clock.
        if (form.walkthrough_date && !form.walkthrough_time) errs.walkthrough_time = 'Pick a time';
        setErrors(errs);
        // Don't let a required-field error hide inside a collapsed section.
        if (errs.name || errs.phone) setOpenSections(s => ({ ...s, contact: true }));
        return Object.keys(errs).length === 0;
    };
    // Synchronous view of validate()'s result (state lands next render).
    const errorsNow = () => {
        const e = {};
        if (!form.name?.trim()) e.name = 'Required';
        if (!normalizePhonesForSave(form.phones?.length ? form.phones : phonesOf(form)).length) e.phone = 'Required';
        if (!form.origin_address?.trim()) e.origin_address = 'Required';
        if (form.walkthrough_date && !form.walkthrough_time) e.walkthrough_time = 'Pick a time';
        return e;
    };
    const ERROR_LABELS = { name: 'Full name', phone: 'Phone number', origin_address: 'Pickup address', walkthrough_time: 'Walkthrough time' };
    const errorItems = Object.entries(errors).filter(([, v]) => v).map(([k, v]) => `${ERROR_LABELS[k] || k} — ${String(v).toLowerCase()}`);
    // Duplicate-create guard (2026-07-02). STRONG match → dialog with
    // three ways out; SOFT (name-only) → non-blocking toast after save.
    const [dupePrompt, setDupePrompt] = useState(null);
    const dupeOverrideRef = useRef(false);

    // Quick Dispatch (2026-08-06) — the small-job fast path. Holds the
    // client object to dispatch; also remembers whether we got here from
    // a fresh intake save (in which case closing the quick modal closes
    // the whole intake — the operator is done either way).
    const [quickDispatch, setQuickDispatch] = useState(null);
    const quickFromIntakeRef = useRef(false);
    // "Alternate pricing" (Estimates tab → New Estimate tab, 2026-09-18):
    // the option to copy pricing from; `nonce` re-applies on every click.
    const [pricingFrom, setPricingFrom] = useState(null);

    const handleSave = async (opts = {}) => {
        // opts.stayOpen: silent auto-commit fired right after an estimate is
        // created from the Pricing tab — persist the form and clear the
        // unsaved-changes guard, but DON'T close the modal or flip to view
        // mode (the operator just landed on the Estimates tab).
        // opts.auto: an autosave tick (feedback.md 5C) — no toast, no button
        // spinner, no mode change; returns true or a short reason string.
        const auto = !!opts.auto;
        if (!validate()) {
            if (!auto) return;
            const first = ['phone', 'name', 'origin_address', 'walkthrough_time'].find(k => errorsNow()[k]);
            return first ? `${(ERROR_LABELS[first] || first).toLowerCase()} ${String(errorsNow()[first]).toLowerCase()}` : 'fix the fields above';
        }
        if (!auto) setSaving(true);
        const cleaned = { ...form };
        // Multi-phone sync (sql/068): normalized array + legacy phone
        // column mirroring the primary (keeps SMS/search/dispatch working).
        cleaned.phones = normalizePhonesForSave(cleaned.phones?.length ? cleaned.phones : phonesOf(cleaned));
        cleaned.phone = cleaned.phones.find(p => p.is_primary)?.number || '';

        // Duplicate check — create only, once (Create Anyway sets override).
        if (!client?.id && !dupeOverrideRef.current) {
            const dupe = findLikelyDuplicates(cleaned, allClients);
            if (dupe?.strength === 'strong') {
                setDupePrompt(dupe);
                setSaving(false);
                return;
            }
            if (dupe?.strength === 'soft') {
                // Heads-up only — save proceeds. Fire it a beat AFTER the
                // parent's "client added" success toast (and give it a longer
                // life) so it isn't visually stomped by it (NEW-2, 2026-07-07).
                const dupName = dupe.match.name;
                setTimeout(() => show(`Heads up: an existing client is also named ${dupName}.`, 'warning', { duration: 7000 }), 1200);
            }
        }
        // Store walkthrough scheduling info locally, strip from client save (stored on job instead)
        const wtTime = cleaned.walkthrough_time || '';
        const wtAssignedTo = cleaned.walkthrough_assigned_to || '';
        delete cleaned.walkthrough_time;
        delete cleaned.walkthrough_assigned_to;
        // New intake: the trip-strip draft is what the client insert carries
        // (the DB seeds the lead move from these columns, 2026-09-18).
        if (!client?.id) {
            cleaned.move_date = draft.load_date || null;
            cleaned.move_time = draft.load_time || '';
            cleaned.date_tentative = !!draft.load_tentative;
            cleaned.pack_date = draft.pack_date || null;
            cleaned.pack_time = draft.pack_time || '';
        }
        ['move_date', 'walkthrough_date', 'pack_date'].forEach(k => { if (cleaned[k] === '' || cleaned[k] === undefined) cleaned[k] = null; });
        // Packing day only matters when packing is actually selected.
        if (cleaned.packing_needed !== 'partial' && cleaned.packing_needed !== 'full') { cleaned.pack_date = null; cleaned.pack_time = ''; }
        ['from_sqft', 'from_levels', 'to_sqft', 'to_levels'].forEach(k => { if (cleaned[k] === '' || cleaned[k] === undefined) cleaned[k] = 0; });
        try {
            // Existing client: split the payload (moves-architecture.md rule
            // 1). Identity — addresses, stops + their access details, type,
            // packing flag, heavy items, walkthrough — goes to the CURRENT
            // MOVE; people fields go to the client as before (saveClient
            // strips the mirror keys anyway). Dates are NOT in this patch:
            // the MoveHeaderCard writes them straight to the move, and the
            // form's move_date/pack_date copies may be stale. Only changed
            // keys are sent so an autosave of a phone number doesn't re-fire
            // the mirror across every option.
            if (client?.id && current?.id) {
                const patch = moveFieldsFromClientShape(cleaned);
                MOVE_DATE_FIELDS.forEach(k => { delete patch[k]; });
                Object.keys(cleaned).forEach(k => { if (/^stop_\d+_[a-z_]+$/.test(k)) patch[k] = cleaned[k]; });
                const same = (a, b) => (a === b) || ((a === null || a === undefined || a === '') && (b === null || b === undefined || b === ''))
                    || (typeof a === 'object' && typeof b === 'object' && JSON.stringify(a) === JSON.stringify(b));
                const changed = {};
                const curVal = (k) => (k in current ? current[k] : current.stop_details?.[k]); // stop_N_* live in stop_details jsonb
                Object.keys(patch).forEach(k => { if (!same(patch[k], curVal(k))) changed[k] = patch[k]; });
                if (Object.keys(changed).length) await saveMove({ id: current.id, ...changed });
            }
            const savedClient = await onSave(cleaned, { silent: auto || !!opts.stayOpen });
            const clientId = savedClient?.id || client?.id;
            // New intake: the fields the client row can't carry go to the
            // lead move the insert just created (unload day / window,
            // pack tentative). Best-effort — the client + move exist.
            if (!client?.id && savedClient?.current_move_id) {
                const extra = {};
                if (draft.unload_date) { extra.unload_date = draft.unload_date; extra.unload_window_end = draft.unload_window_end || null; extra.unload_time = draft.unload_time || null; extra.unload_tentative = !!draft.unload_tentative; }
                if (cleaned.pack_date && draft.pack_tentative) extra.pack_tentative = true;
                if (Object.keys(extra).length) {
                    try { await saveMove({ id: savedClient.current_move_id, ...extra }); }
                    catch (e) { show('Client saved, but the unload / pack dates didn\'t stick: ' + e.message, 'error'); }
                }
            }

            // Create/update walkthrough job on dispatch board if walkthrough is scheduled
            if (cleaned.walkthrough_date && clientId) {
                const existingWalkthroughJobs = (modalJobs || []).filter(j => j.client_id === clientId && j.type === 'WALKTHROUGH');
                const walkthroughJob = {
                    ...(existingWalkthroughJobs[0] || {}),
                    client_id: clientId,
                    type: 'WALKTHROUGH',
                    job_label: 'Walkthrough',
                    move_date: cleaned.walkthrough_date,
                    time: wtTime || '09:00',
                    from_address: cleaned.origin_address || '',
                    to_address: cleaned.destination_address || '',
                    status: wtAssignedTo ? 'assigned' : 'unassigned',
                    leader_id: wtAssignedTo || null,
                    crew_ids: wtAssignedTo ? [wtAssignedTo] : [],
                    truck_ids: [],
                    duration_days: 1,
                    packing_service: false,
                };
                await saveJob(walkthroughJob);
            }

            // New-client notes → create the first entries now that we have an id.
            if (!client?.id && clientId && pendingCrewNote.body?.trim() && saveCrewNoteSet) {
                try {
                    await saveCrewNoteSet({
                        client_id: clientId,
                        label: (pendingCrewNote.label || '').trim() || `Crew notes · ${new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`,
                        body: pendingCrewNote.body,
                    });
                    setPendingCrewNote({ label: '', body: '' });
                } catch (e) { /* non-fatal — note can be added later from the file */ }
            }
            if (!client?.id && clientId && pendingInternalNote.body?.trim() && saveInternalNoteSet) {
                try {
                    await saveInternalNoteSet({
                        client_id: clientId,
                        label: (pendingInternalNote.label || '').trim() || null,
                        body: pendingInternalNote.body,
                    });
                    setPendingInternalNote({ label: '', body: '' });
                } catch (e) { /* non-fatal — note can be added later from the file */ }
            }

            setDirty(false);
            // Quick Dispatch auto-offer (2026-08-06): a brand-new client
            // with a small job type + a real date can go straight onto the
            // dispatch board — offer it right here instead of making the
            // operator find the fast path later. Cancelling ("Not now")
            // just closes everything; nothing is created until Send.
            if (!client?.id && clientId && QUICK_DISPATCH_TYPES.includes(cleaned.job_type) && cleaned.move_date && !cleaned.date_tentative) {
                quickFromIntakeRef.current = true;
                setQuickDispatch({ ...cleaned, id: clientId });
            } else if (auto || opts.stayOpen) { /* autosave / estimate-created auto-commit — stay put */ }
            else if (!initialViewMode) onClose(); else setEditing(false);
            return true;
        } catch (e) { if (auto) return e.message || 'save failed'; show('Error: ' + e.message, 'error'); }
        finally { if (!auto) setSaving(false); }
    };
    const autosave = useAutosave({ save: () => handleSave({ auto: true }), enabled: !!client?.id });
    autosaveRef.current = autosave;
    const errCls = (field) => errors[field] ? 'border-red-400 ring-1 ring-red-400' : '';

    // Cancel walkthrough = dead lead (Jaylen 2026-06-09): the client goes
    // inactive (drops out of every dashboard queue, incl. the 30-min
    // auto-promotion) and the WALKTHROUGH dispatch job is removed.
    // Partial upsert on purpose — doesn't sweep up any half-typed edits
    // sitting in the form.
    const [cancellingWt, setCancellingWt] = useState(false);
    // In-app confirms (were window.confirm — off-brand + block the page
    // invisibly; NEW-1, 2026-07-07). Gate the destructive action behind
    // ConfirmDialog, matching the unsaved-changes guard.
    const [cancelWtConfirm, setCancelWtConfirm] = useState(false);
    // (clearContactConfirm removed 2026-08-07 — first-contact stamp retired.)
    const handleCancelWalkthrough = async () => {
        if (!client?.id) return;
        setCancelWtConfirm(false);
        setCancellingWt(true);
        try {
            await onSave({
                id: client.id,
                status: 'inactive',
                walkthrough_date: null,
                walkthrough_completed_at: null,
                walkthrough_status: 'not_started',
            });
            // The walkthrough lives on the move (2026-09-18); saveClient
            // strips those two keys, so clear them there explicitly.
            if (current?.id) await saveMove({ id: current.id, walkthrough_date: null, walkthrough_status: 'not_started' });
            const wtJobs = (modalJobs || []).filter(j => j.client_id === client.id && j.type === 'WALKTHROUGH');
            for (const j of wtJobs) {
                try { await removeJob(j.id); } catch (e) { console.warn('WT job removal failed:', e); }
            }
            onClose();
        } catch (e) {
            show('Error cancelling walkthrough: ' + e.message, 'error');
        } finally {
            setCancellingWt(false);
        }
    };

    // Reschedule = client stays active; clear the completed stamp (so the
    // pipeline returns them to "Walkthroughs Scheduled" instead of
    // Brothers' Review) and the time, then let the user pick fresh
    // date/time and Save. setWalkthrough auto-enters edit mode.
    const handleRescheduleWalkthrough = () => {
        setWalkthrough('walkthrough_completed_at', null);
        setWalkthrough('walkthrough_completed_by', null);
        setWalkthrough('walkthrough_status', 'not_started');
        setWalkthrough('walkthrough_time', '');
    };

    // One-line summaries shown on a collapsed section header.
    const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    const statusLabel = (clientStatuses.find(s => (s.key || s.value) === form.status)?.label) || (form.status || '').replace(/_/g, ' ');
    const contactSummary = [form.name, form.phone].filter(Boolean).join(' · ');
    const jobSummary = [
        (form.job_type || 'LOCAL').replace(/_/g, ' '),
        form.packing_needed && form.packing_needed !== 'tbd' ? (PACKING_LABELS[form.packing_needed] || form.packing_needed) : '',
    ].filter(Boolean).join(' · ');
    const adminSummary = [statusLabel, cap(form.lead_source)].filter(Boolean).join(' · ');
    const sectionMeta = { contact: contactSummary, job: jobSummary, admin: adminSummary };
    // The move header card's Edit → the address / access editor below it.
    const moveCardRef = useRef(null);
    const addressesRef = useRef(null);
    const scrollToAddresses = () => { try { addressesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* no-op */ } };
    const formatDateDisplay = (dateStr) => {
        if (!dateStr) return null;
        try { const d = new Date(dateStr + 'T12:00:00'); return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); }
        catch(e) { return dateStr; }
    };


    // ── Slide-over chrome (client-file.md, Jaylen 2026-09-14 pick B) ────
    // Header: name + status + the two actions; strip: storage banner + tabs;
    // body: the active tab; footer: pinned Cancel/Save so it's never
    // off-screen. Photos / Notes / Activity moved out of the Details scroll
    // into their own tabs.
    const headerStatus = clientStatuses.find(s => (s.key || s.value) === form.status);
    // Subline reads the CURRENT MOVE (2026-09-18): "Pack Sep 14 · Load Sep 16
    // · Unload ~Sep 20" — each date wears its own tentative marker.
    const subline = [
        current ? jobTypeShort(current.job_type) : (form.job_type || 'LOCAL').replace(/_/g, ' '),
        formatPhone(form.phone),
        current ? (current.load_date ? moveDatesSummary(current) : null)
            : (form.move_date ? `Move ${formatDateDisplay(form.move_date)}${form.date_tentative ? ' (tentative)' : ''}` : null),
    ].filter(Boolean).join(' · ');
    const header = (
        <React.Fragment>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                    <h2 className="text-[17px] font-bold text-slate-900 truncate leading-tight">{isExisting ? (form.name || client.name || 'Client') : 'New Client Intake'}</h2>
                    {isExisting && headerStatus && (
                        <span className="flex-shrink-0 text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md" style={{ background: statusTint(headerStatus.color, headerStatus.bg), color: headerStatus.color }}>{headerStatus.label}</span>
                    )}
                </div>
                {isExisting && <div className="text-[11.5px] text-slate-500 truncate mt-0.5">{subline}</div>}
            </div>
            {isExisting && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Quick Dispatch (2026-08-06) — small-job fast path, on
                        EVERY client file (add-on dump runs etc. — see
                        changelog). "Mark First Contact Made" retired
                        2026-08-07: creating the file IS first contact. */}
                    {!editing && (
                        <Button variant="success" size="sm" onClick={() => { quickFromIntakeRef.current = false; setQuickDispatch(client); }}
                            title="Delivery, dump run, labor only, pickup — books a small job straight onto the dispatch board">
                            ⚡<span className="hidden sm:inline ml-1">Quick Dispatch</span>
                        </Button>
                    )}
                    {!editing && (
                        <Button size="sm" leftIcon={<IconEdit size={14}/>} onClick={() => setEditing(true)} title="Edit client">
                            <span className="hidden sm:inline">Edit</span>
                        </Button>
                    )}
                    {(neighbors?.prev || neighbors?.next) && (
                        <div className="hidden md:flex items-center gap-0.5 ml-1 pl-1.5 border-l border-slate-200">
                            <IconButton label="Previous client" size="sm" className="bbm-tip-below" disabled={!neighbors?.prev} onClick={() => switchTo(neighbors.prev)}><IconChevronLeft size={16}/></IconButton>
                            <IconButton label="Next client" size="sm" className="bbm-tip-below bbm-tip-left" disabled={!neighbors?.next} onClick={() => switchTo(neighbors.next)}><IconChevronRight size={16}/></IconButton>
                        </div>
                    )}
                </div>
            )}
        </React.Fragment>
    );
    const TABS = isExisting ? [
        { id: 'details', label: 'Client Details' },
        { id: 'pricing', label: 'New Estimate' },
        // Counts the CURRENT move's options — that's what the tab lists.
        { id: 'estimates', label: `Estimates (${current ? currentOptions.length : clientEstimates.length})` },
        { id: 'photos', label: photos.length ? `Photos (${photos.length})` : 'Photos' },
        { id: 'notes', label: 'Notes' },
        { id: 'activity', label: 'Activity' },
    ] : [];
    const strip = (
        <React.Fragment>

            {/* Storage banner (sql/084-085, 2026-08-27): status-aware —
                QUOTED (amber, nothing stored yet — just being estimated
                into storage) vs IN STORAGE (indigo, items physically in
                the unit). Management lives on the Storage page. */}
            {client?.id && (storageTenancies || []).filter(u => u.client_id === client.id && u.status !== 'closed').map(u => {
                const isQuoted = u.status === 'quoted';
                const place = tenancyPlace(u, byId(storageUnits), byId(storageFacilities));
                const paid = !isQuoted ? paidThruStatus(u.paid_thru) : null;
                return (
                    <div key={u.id} className={`flex items-center gap-2 flex-wrap px-4 md:px-5 py-2 border-b text-[12px] ${isQuoted ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-indigo-50 border-indigo-200 text-indigo-800'}`}>
                        <span className={`font-black uppercase tracking-wide text-[10px] text-white px-2 py-0.5 rounded-full ${isQuoted ? 'bg-amber-500' : 'bg-indigo-600'}`}>
                            {isQuoted ? '📦 Storage Quoted' : '📦 In Storage'}
                        </span>
                        <span className="font-semibold">{place}</span>
                        <span className={isQuoted ? 'text-amber-600' : 'text-indigo-500'}>
                            {u.ownership === 'bbm' ? 'BBM unit' : "client's own"}
                            {!isQuoted && ` · ${u.fullness === 0 ? 'empty' : u.fullness === 100 ? 'full' : `${u.fullness}% full`}`}
                            {isQuoted && ' · goes active when the move-in completes'}
                        </span>
                        {u.expected_out && <span className={isQuoted ? 'text-amber-600' : 'text-indigo-500'}>out: {u.expected_out}</span>}
                        {paid && paid.tone === 'overdue' && <span className="text-[10px] font-bold text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded">rent {paid.label}</span>}
                        {/* Real link (Jaylen, 9/8 Browser-pane test): closes the
                            client file and lands on the Storage page. Asks the
                            unsaved-changes guard first so a mid-edit form
                            isn't silently thrown away. */}
                        <button type="button"
                            onClick={() => { if (!beforeClose()) return; onClose(); setCurrentPage?.('storage'); }}
                            className={`ml-auto underline underline-offset-2 font-semibold ${isQuoted ? 'text-amber-600 hover:text-amber-800' : 'text-indigo-600 hover:text-indigo-800'}`}>
                            manage on the Storage page →
                        </button>
                    </div>
                );
            })}
            {/* Move switcher (pick 3B, 2026-09-18): which move the file
                shows — dates block, quotes, estimate tab. One-move clients
                see one chip. "+ New move" opens a small menu: blank, or
                "Deliver out of storage" chained to the current move. */}
            {isExisting && (
                <div className="px-4 md:px-5 py-2 border-b border-slate-100 bg-slate-50/60 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                    <MoveSwitcher
                        moves={clientMoves}
                        currentId={current?.id}
                        onSelect={selectMove}
                        onNew={() => setNewMoveOpen(o => !o)}
                        newMenu={(
                            <React.Fragment>
                                <button ref={newMoveRef} type="button" onClick={() => setNewMoveOpen(o => !o)} aria-haspopup="menu" aria-expanded={newMoveOpen}
                                    className="h-8 px-2.5 rounded-lg border border-dashed border-slate-300 text-[12.5px] font-bold text-slate-500 hover:text-slate-800 hover:border-slate-500 whitespace-nowrap">
                                    + New move
                                </button>
                                <PopoverPanel open={newMoveOpen} onClose={() => setNewMoveOpen(false)} anchorRef={newMoveRef} role="menu" ariaLabel="New move">
                                    <div className="p-1.5 w-[240px]">
                                        <button type="button" role="menuitem" onClick={() => newMove('blank')} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100">
                                            <span className="block text-[13px] font-bold text-slate-800">New move (blank)</span>
                                            <span className="block text-[11px] text-slate-500">Another job for this client — fresh dates and addresses.</span>
                                        </button>
                                        {offerStorageOut && (
                                            <button type="button" role="menuitem" onClick={() => newMove('storage')} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100">
                                                <span className="block text-[13px] font-bold text-slate-800">Deliver out of storage</span>
                                                <span className="block text-[11px] text-slate-500">Starts where this move ends — origin pre-filled, chained to it.</span>
                                            </button>
                                        )}
                                    </div>
                                </PopoverPanel>
                            </React.Fragment>
                        )}
                    />
                </div>
            )}
            {isExisting && (
                <div className="flex gap-0.5 px-2 md:px-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }} role="tablist">
                    {TABS.map(t => (
                        <button key={t.id} type="button" role="tab" aria-selected={activeTab === t.id} data-testid={`client-tab-${t.id}`} onClick={() => setActiveTab(t.id)}
                            className={`px-3 py-2.5 text-[13px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${activeTab === t.id ? 'text-red-600 border-red-600' : 'text-slate-400 border-transparent hover:text-slate-600'}`}>
                            {t.label}
                        </button>
                    ))}
                </div>
            )}
        </React.Fragment>
    );
    const footer = (
        <div className="flex items-center gap-2">
            {isExisting && (editing || autosave.savedAt) && <AutosaveStatus status={editing ? autosave.status : 'idle'} savedAt={autosave.savedAt} error={autosave.error} />}
            <div className="ml-auto flex items-center gap-2">
                {isExisting && editing ? (
                    <React.Fragment>
                        {/* feedback.md 5C: edits autosave; Done just locks the
                            fields again (after flushing anything pending). */}
                        <Button onClick={async () => { const ok = await autosave.flush(); if (ok) { setDirty(false); setEditing(false); await discardUntouchedMove(); } }}>Done</Button>
                    </React.Fragment>
                ) : isExisting ? (
                    <React.Fragment>
                        <Button variant="secondary" onClick={handleClose}>Close</Button>
                        <Button onClick={() => setEditing(true)} leftIcon={<IconEdit size={15}/>}>Edit Client</Button>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                        <Button onClick={handleSave} loading={saving}>Save Client</Button>
                    </React.Fragment>
                )}
            </div>
        </div>
    );

    return (
        <SlideOver label={isExisting ? `Client file — ${client.name || ''}` : 'New client intake'} onClose={onClose} onBeforeClose={beforeClose} closeRef={closeRef} header={header} strip={strip} footer={footer}>
            {(!isExisting || activeTab === 'details') && (
            <>
            {errorItems.length > 0 && <FormErrorBanner items={errorItems} title={isExisting ? `Fix ${errorItems.length} thing${errorItems.length === 1 ? '' : 's'} to save` : undefined} className="mb-4" />}
            <div className={readOnly ? 'pointer-events-none opacity-90' : ''} style={readOnly ? { pointerEvents: 'none' } : {}}>
            <div className="space-y-5">

                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                        <button type="button" onClick={() => toggleSection('contact')} className="w-full flex items-center justify-between gap-2 text-left" style={{ pointerEvents: 'auto' }}>
                            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2"><IconPhone size={13}/>Contact</h3>
                            <div className="flex items-center gap-2 min-w-0">
                                {!openSections.contact && sectionMeta.contact && <span className="text-xs text-slate-500 truncate max-w-[200px]">{sectionMeta.contact}</span>}
                                <span className={`text-slate-400 text-[10px] transition-transform ${openSections.contact ? 'rotate-180' : ''}`}>▼</span>
                            </div>
                        </button>
                        {/* Condensed contact block (Jaylen 2026-09-17): label-left
                            rows — Full name / Phone # (type keys on the same
                            row) / Email — and a real button for the secondary
                            contact, so the move date sits higher on the form.
                            The SMS-approved toggle (a hold-over from the first
                            intake form) is gone; `sms_approved` stays in the
                            data untouched. */}
                        {openSections.contact && (<div className="mt-3">
                        <FormField layout="row" label="Full name" required error={errors.name}><input className={`${inputCls} ${errCls('name')} text-base font-medium`} value={form.name} onChange={e => { set('name', e.target.value); setErrors(p => ({...p, name: false})); }} placeholder="John Smith" readOnly={readOnly} /></FormField>
                        {/* Multi-phone (sql/068): star = primary — what the
                            system calls/texts/displays. */}
                        <FormField layout="row" label="Phone #" required error={errors.phone}>
                            <PhonesEditor
                                inline
                                value={(form.phones?.length ? form.phones : phonesOf(form)).map(p => ({ ...p, number: formatPhone(p.number) }))}
                                onChange={(rows) => { set('phones', rows); setErrors(p => ({ ...p, phone: false })); }}
                                inputCls={inputCls}
                                error={errors.phone}
                            />
                        </FormField>
                        <FormField layout="row" label="Email"><input className={inputCls} type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} placeholder="john@email.com" /></FormField>

                        {/* Secondary contact — e.g. spouse. Low-frequency, so it's
                            collapsed behind a button (auto-open when data exists).
                            Stored + searchable (looking up this name/number
                            surfaces this client). */}
                        {!showSecondary ? (
                            <div className="sm:pl-[108px]">
                                <Button variant="secondary" size="sm" onClick={() => setShowSecondary(true)}>
                                    + Add secondary contact
                                </Button>
                            </div>
                        ) : (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                                    Secondary Contact <span className="text-slate-300 normal-case font-normal">(optional — e.g. spouse)</span>
                                </div>
                                <FormField layout="row" label="Name"><input className={inputCls} value={form.secondary_name || ''} onChange={e => set('secondary_name', e.target.value)} placeholder="Jane Smith" readOnly={readOnly} /></FormField>
                                <FormField layout="row" label="Phone"><input className={inputCls} type="tel" inputMode="tel" value={form.secondary_phone || ''} onChange={e => set('secondary_phone', maskPhoneInput(e.target.value))} placeholder="(615) 555-0001" readOnly={readOnly} /></FormField>
                                <FormField layout="row" label="Email"><input className={inputCls} type="email" value={form.secondary_email || ''} onChange={e => set('secondary_email', e.target.value)} placeholder="jane@email.com" readOnly={readOnly} /></FormField>
                                <p className="text-[10px] text-slate-400 sm:pl-[108px]">Searchable — looking up this name or number pulls up this client's file.</p>
                            </div>
                        )}
                        </div>)}
                    </div>

                    <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2"><IconTruck size={13}/>The Move</h3>

                    {/* Move dates (2026-09-18, moves-architecture.md §4 pick 1B).
                          EXISTING client: the move header card — trip strip
                          (Pack · Load · Unload, each tentative on its own),
                          From / Stops / To, type, status — writing straight to
                          the CURRENT MOVE (saveMove, no debounce). Its Edit
                          button scrolls to the address editor below, which
                          autosaves to the same move through handleSave.
                          NEW intake: the same strip over a local draft that
                          handleSave maps onto the client insert. The old
                          amber Move Date card (DatePicker + tentative
                          checkbox) and the Packing Day fields are gone —
                          the strip is the one date control. */}
                    {isExisting ? (
                        current ? (
                            <div ref={moveCardRef} style={{ pointerEvents: 'auto' }}>
                                <MoveHeaderCard
                                    move={{ ...current, job_type: form.job_type || current.job_type, packing_needed: form.packing_needed ?? current.packing_needed }}
                                    disabled={readOnly}
                                    onChange={(patch) => saveMove({ id: current.id, ...patch }).catch(e => show('Couldn\'t save the date: ' + e.message, 'error'))}
                                    onEdit={scrollToAddresses}
                                    extra={<>
                                        {/* Available in view mode too — removing a stray move is a
                                            one-off action like scheduling a walkthrough, not an edit. */}
                                        {canRemoveMove && <Button size="sm" variant="tertiary" onClick={() => setRemoveMoveConfirm(true)} title="Remove this move — it has no quote and nothing on the board">Remove move</Button>}
                                        {readOnly ? <Button size="sm" variant="secondary" onClick={() => { setEditing(true); setTimeout(scrollToAddresses, 50); }}>Edit</Button> : null}
                                    </>}
                                />
                            </div>
                        ) : (
                            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-[13px] text-slate-500" style={{ pointerEvents: 'auto' }}>
                                No move on file yet — <button type="button" className="font-bold text-slate-800 underline underline-offset-2" onClick={() => newMove('blank')}>add one</button> to set dates and addresses.
                            </div>
                        )
                    ) : (
                        <TripStrip
                            value={{ ...draft, job_type: form.job_type, packing_needed: form.packing_needed }}
                            onChange={patchDraft}
                            disabled={readOnly}
                            label="Move dates"
                        />
                    )}

                    <div ref={addressesRef} />
                    {/* Keyed by editorSeed so the route editor re-hydrates its
                        origin / stops / destination when the switcher changes
                        moves (its route state is seeded once on mount). */}
                    {/* setBatch is the route editor's write path (origin / stops /
                        destination land together). It has to mark the form dirty
                        and poke the autosave like `set` does — otherwise an
                        address edit never saves (found 2026-09-18 E2E). The
                        editor's mount effect re-sends the current values, so
                        only a real change counts. */}
                    <MoveDetailsEditor key={editorSeed} form={form} set={(k, v) => set(k, v)} setBatch={(updates) => {
                        setForm(f => {
                            const changed = Object.keys(updates).some(k => (f[k] ?? '') !== (updates[k] ?? ''));
                            if (!changed) return f;
                            setDirty(true);
                            if (client?.id) autosaveRef.current?.touch();
                            return { ...f, ...updates };
                        });
                    }} readOnly={readOnly} />

                    {/* New-client notes — office + crew captured here and saved
                        as the client's first entries on Save (rows need a
                        client_id, which doesn't exist until then). Existing
                        clients use the full live <ClientNotesPanel> below. */}
                    {!client?.id && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">📝 Notes</span>
                            <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Office Notes</span>
                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-600">OFFICE ONLY</span>
                                </div>
                                <textarea className={`${inputCls} text-xs`} rows={2} placeholder="Internal office note — never shown to the crew…" value={pendingInternalNote.body} onChange={e => setPendingInternalNote(n => ({ ...n, body: e.target.value }))} />
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Crew Notes</span>
                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600">REACHES CREW</span>
                                </div>
                                <textarea className={`${inputCls} text-xs`} rows={2} placeholder="Notes for the crew — gate code, where to park, pets…" value={pendingCrewNote.body} onChange={e => setPendingCrewNote(n => ({ ...n, body: e.target.value }))} />
                            </div>
                            <p className="text-[10px] text-slate-400">Optional — saved with this client. After saving you can add more, timestamped, anytime.</p>
                        </div>
                    )}

                    {/* Walkthrough Toggle — always shown, including in view
                        mode. Scheduling a walkthrough is a high-frequency
                        one-off action; gating it behind "Edit Client" used
                        to make the button invisible on existing clients
                        (May 2026 UX fix). The setWalkthrough() setter
                        auto-flips us into edit mode so the Save button
                        appears at the bottom once the user starts changing
                        fields. */}
                    <div className={`rounded-lg border ${form.walkthrough_date ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'} p-3`} style={{ pointerEvents: 'auto' }}>
                        {/* Walkthrough status (sql/064) — set by the estimator app.
                            Not started → In progress → Complete, with who + when. */}
                        {form.walkthrough_status && form.walkthrough_status !== 'not_started' && (() => {
                            const done = form.walkthrough_status === 'completed';
                            const stamp = (t) => t ? new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
                            const who = done ? form.walkthrough_completed_by : form.walkthrough_started_by;
                            const when = done ? stamp(form.walkthrough_completed_at) : stamp(form.walkthrough_started_at);
                            return (
                                <div className="mb-2 flex items-center gap-2 flex-wrap">
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {done ? '✓ WALKTHROUGH COMPLETE' : 'WALKTHROUGH IN PROGRESS'}
                                    </span>
                                    {(who || when) && (
                                        <span className="text-[10px] text-slate-400">
                                            {who ? `${done ? 'by' : 'started by'} ${who}` : ''}{when ? ` · ${when}` : ''}
                                        </span>
                                    )}
                                </div>
                            );
                        })()}
                        {/* Explicit expand/collapse instead of the old checkbox
                            (Jaylen 2026-07-02: it "toggled strangely" and once
                            the time-required validation kicked in there was no
                            obvious way back out). "+ Schedule" expands; the
                            "Don't schedule" escape clears the fields — and the
                            validation block — for the changed-my-mind case.
                            Clients with an already-persisted walkthrough keep
                            the Reschedule / Cancel links below instead. */}
                        {!form.walkthrough_date ? (
                            <Button
                                variant="secondary"
                                full
                                onClick={() => {
                                    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
                                    setWalkthrough('walkthrough_date', tomorrow.toISOString().split('T')[0]);
                                }}
                            >
                                + Schedule Walkthrough
                            </Button>
                        ) : (
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-slate-700">Walkthrough</span>
                                <div className="flex items-center gap-2">
                                    <button type="button" onClick={() => setWalkthrough('virtual_walkthrough', !form.virtual_walkthrough)}
                                        className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${form.virtual_walkthrough ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-300 hover:text-slate-400'}`}>
                                        Virtual
                                    </button>
                                    {!client?.walkthrough_date && (
                                        <Button
                                            variant="tertiary"
                                            size="sm"
                                            onClick={() => {
                                                setWalkthrough('walkthrough_date', null);
                                                setWalkthrough('walkthrough_time', '');
                                                setWalkthrough('walkthrough_assigned_to', '');
                                                setErrors(e => ({ ...e, walkthrough_time: false }));
                                            }}
                                            title="Changed my mind — not scheduling right now"
                                        >
                                            ✕ Don't schedule
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                        {form.walkthrough_date && (
                            <div className="grid grid-cols-3 gap-3 mt-3">
                                <FormField label="Date">
                                    <DatePicker className={`${inputCls} cursor-pointer`} value={form.walkthrough_date || ''} onChange={v => setWalkthrough('walkthrough_date', v)} placeholder="Select date..." />
                                </FormField>
                                <FormField label="Time" required error={errors.walkthrough_time}>
                                    <TimePicker
                                        className={errCls('walkthrough_time')}
                                        value={form.walkthrough_time || ''}
                                        onChange={v => setWalkthrough('walkthrough_time', v)}
                                        from={7}
                                        to={19}
                                        placeholder="Select time..."
                                        ariaLabel="Walkthrough time"
                                    />
                                </FormField>
                                <FormField label="Assigned To">
                                    <Combobox
                                        items={walkthroughAssignees.map(c => ({ value: c.id, label: c.name, sub: formatPhone(c.phone) || undefined }))}
                                        value={form.walkthrough_assigned_to || ''}
                                        onChange={v => setWalkthrough('walkthrough_assigned_to', v)}
                                        placeholder="TBD"
                                        ariaLabel="Walkthrough assigned to"
                                    />
                                </FormField>
                            </div>
                        )}
                        {/* In-card Schedule button (Jaylen 2026-08-10): saving a
                            walkthrough used to require scrolling to the bottom
                            Save button — the action now lives where the fields
                            are. Same handleSave as the footer (full validation,
                            creates/updates the board's WALKTHROUGH job, and the
                            auto-working trigger promotes new leads). Shown only
                            while editing with a date picked and unsaved changes. */}
                        {editing && form.walkthrough_date && dirty && (
                            <div className="flex justify-end mt-3">
                                <Button loading={saving} onClick={handleSave}>
                                    📅 {client?.walkthrough_date ? 'Update Walkthrough' : 'Schedule Walkthrough'}
                                </Button>
                            </div>
                        )}
                        {/* Cancel / Reschedule — existing clients with a scheduled
                            walkthrough (Jaylen 2026-06-09). Cancel = dead lead →
                            inactive; Reschedule = clear the completed stamp + time
                            so the client returns to "Walkthroughs Scheduled" once
                            new date/time are saved. */}
                        {client?.id && form.walkthrough_date && (
                            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-purple-200/60">
                                <Button
                                    variant="tertiary"
                                    size="sm"
                                    onClick={handleRescheduleWalkthrough}
                                    title="Client stays active — pick a new date & time, then Save"
                                >
                                    Reschedule
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => setCancelWtConfirm(true)}
                                    loading={cancellingWt}
                                    title="Marks the client inactive and removes the walkthrough from dispatch"
                                >
                                    Cancel Walkthrough (client inactive)
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                        <button type="button" onClick={() => toggleSection('job')} className="w-full flex items-center justify-between gap-2 text-left" style={{ pointerEvents: 'auto' }}>
                            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2"><IconClipboard size={13}/>Job & Service</h3>
                            <div className="flex items-center gap-2 min-w-0">
                                {!openSections.job && sectionMeta.job && <span className="text-xs text-slate-500 truncate max-w-[200px]">{sectionMeta.job}</span>}
                                <span className={`text-slate-400 text-[10px] transition-transform ${openSections.job ? 'rotate-180' : ''}`}>▼</span>
                            </div>
                        </button>
                        {openSections.job && (<div className="mt-3">
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Job Type">
                                <Combobox
                                    items={JOB_TYPES.map(t => ({ value: t, label: t.replace('_', ' ') }))}
                                    value={form.job_type || 'LOCAL'}
                                    onChange={v => set('job_type', v)}
                                    clearable={false}
                                    placeholder="Job type…"
                                />
                            </FormField>
                            <FormField label="Packing">
                                <ChoiceKeys
                                    items={PACKING_OPTIONS}
                                    value={form.packing_needed || 'tbd'}
                                    onChange={v => set('packing_needed', v)}
                                />
                            </FormField>
                        </div>
                        {/* Packing DAY moved into the trip strip above (the Pack
                            tile appears once partial/full packing is picked;
                            2026-09-18) — one date control, no second field. */}
                        <FormField label="Heavy / Special Items"><input className={inputCls} value={form.heavy_items || ''} onChange={e => set('heavy_items', e.target.value)} placeholder="Piano, gun safe, pool table..." /></FormField>
                        </div>)}
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                        <button type="button" onClick={() => toggleSection('admin')} className="w-full flex items-center justify-between gap-2 text-left" style={{ pointerEvents: 'auto' }}>
                            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2"><IconSettings size={13}/>Admin</h3>
                            <div className="flex items-center gap-2 min-w-0">
                                {!openSections.admin && sectionMeta.admin && <span className="text-xs text-slate-500 truncate max-w-[200px]">{sectionMeta.admin}</span>}
                                <span className={`text-slate-400 text-[10px] transition-transform ${openSections.admin ? 'rotate-180' : ''}`}>▼</span>
                            </div>
                        </button>
                        {openSections.admin && (<div className="mt-3">
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Lead Source">
                                <ChoiceKeys
                                    items={LEAD_SOURCES.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                                    value={form.lead_source}
                                    onChange={v => set('lead_source', v)}
                                />
                            </FormField>
                            <FormField label="Client Status">
                                <StatusPicker
                                    statuses={clientStatuses}
                                    value={form.status}
                                    onChange={v => set('status', v)}
                                    mode="dropdown"
                                    disabled={readOnly}
                                />
                            </FormField>
                        </div>
                        <FormField label="Referral"><input className={inputCls} value={form.referral_name || ''} onChange={e => set('referral_name', e.target.value)} placeholder="Who referred them?" /></FormField>
                        {/* Internal notes moved to the unified Notes card below
                            (sql/063) — per-entry, timestamped, addable without
                            entering edit mode. The old single field is retired. */}
                        </div>)}
                    </div>
            </div>
            </div>

            {client?.id && (
                <div className="mt-6 border-t border-slate-200 pt-4 space-y-4" style={{ pointerEvents: 'auto', opacity: 1 }}>

                    {/* Walkthrough Details — shows data filled in from the estimator walkthrough app */}
                    {(form.walkthrough_notes || form.special_notes || form.access_notes || form.from_stairs || form.to_stairs || form.from_elevator || form.to_elevator || ['yes', 'partial', 'full'].includes(form.packing_needed) || form.packing_notes || (form.from_sqft && form.from_sqft > 0)) && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <h3 className="text-xs font-bold text-amber-700 uppercase mb-3 flex items-center gap-2">
                                Walkthrough Details
                                <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-medium normal-case">from estimator</span>
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    {form.from_sqft > 0 && <div className="text-sm text-slate-700"><span className="text-slate-400 font-medium">Sq Ft:</span> {form.from_sqft}</div>}
                                    <div className="flex flex-wrap gap-1.5">
                                        {form.from_stairs && <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Pickup: Stairs</span>}
                                        {form.from_elevator && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Pickup: Elevator</span>}
                                        {form.to_stairs && <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Dest: Stairs</span>}
                                        {form.to_elevator && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Dest: Elevator</span>}
                                        {['yes', 'partial', 'full'].includes(form.packing_needed) && <span className="text-[10px] font-bold bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">{form.packing_needed === 'partial' ? 'Partial Packing' : form.packing_needed === 'full' ? 'Full Packing' : 'Packing Needed'}</span>}
                                    </div>
                                    {form.packing_notes && (
                                        <div className="text-[11px] text-slate-600 mt-1"><span className="text-slate-400 font-semibold">Packing:</span> {form.packing_notes}</div>
                                    )}
                                </div>
                                {form.access_notes && (
                                    <div>
                                        <div className="text-[10px] font-semibold text-slate-400 uppercase">Access & Parking</div>
                                        <p className="text-sm text-slate-700 mt-0.5">{form.access_notes}</p>
                                    </div>
                                )}
                            </div>
                            {/* walkthrough_notes / special_notes retired 2026-07-01 —
                                estimator notes now live in the unified Notes card
                                below (Office/Crew), migrated via sql/066. */}
                        </div>
                    )}

                    {clientJobs.length > 0 && (
                        <div>
                            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                Jobs <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{clientJobs.length}</span>
                            </h3>
                            <div className="space-y-2">
                                {clientJobs.map(job => {
                                    const leader = (modalCrew || []).find(c => c.id === job.leader_id);
                                    const jobCrewNames = (job.crew_ids || []).map(id => (modalCrew || []).find(c => c.id === id)).filter(Boolean);
                                    const jobTruckNames = (job.truck_ids || []).map(id => (modalTrucks || []).find(t => t.id === id)).filter(Boolean);
                                    const isAssigned = job.status === 'assigned' || job.status === 'dispatched';
                                    return (
                                        <div key={job.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-3">
                                                    {leader && <div className="w-2 h-8 rounded-full" style={{backgroundColor: leader.display_color}}></div>}
                                                    <div>
                                                        <div className="text-sm font-medium text-slate-900">{job.type.replace('_',' ')} &bull; {job.move_date} {job.time}</div>
                                                        <div className="text-xs text-slate-600">{job.from_address} &rarr; {job.to_address}</div>
                                                    </div>
                                                </div>
                                                <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${job.status === 'dispatched' ? 'bg-green-100 text-green-700' : job.status === 'assigned' ? 'bg-blue-100 text-blue-700' : job.status === 'completed' ? 'bg-slate-200 text-slate-700' : 'bg-yellow-100 text-yellow-700'}`}>{job.status.replace('_',' ')}</span>
                                            </div>
                                            {isAssigned && (jobCrewNames.length > 0 || jobTruckNames.length > 0) && (
                                                <div className="mt-2 pt-2 border-t border-slate-200 flex flex-wrap gap-3">
                                                    {jobCrewNames.length > 0 && (
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-[10px] text-slate-400 font-semibold uppercase">Crew:</span>
                                                            <div className="flex flex-wrap gap-1">
                                                                {jobCrewNames.map(c => (
                                                                    <span key={c.id} className="text-[10px] px-1.5 py-0.5 rounded-full text-white font-medium" style={{backgroundColor: c.display_color}}>{c.name}{c.id === job.leader_id ? ' (L)' : ''}</span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {jobTruckNames.length > 0 && (
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-[10px] text-slate-400 font-semibold uppercase">Trucks:</span>
                                                            <div className="flex flex-wrap gap-1">
                                                                {jobTruckNames.map(t => (
                                                                    <span key={t.id} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">{t.name}</span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}


            {/* Merge tool — duplicate cleanup (super-admin tier). Everything
                on THIS client moves to the client you pick; this row is
                deleted. Empty fields on the survivor fill from here; phone
                lists union. */}
            {client?.id && canMerge && (
                <div className="mt-6 pt-3 border-t border-slate-100">
                    {!mergeOpen ? (
                        <Button variant="tertiary" size="sm" onClick={() => { setMergeOpen(true); setMergeSearch(''); setMergeTarget(null); }}>
                            This client is a duplicate — merge into another…
                        </Button>
                    ) : (
                        <div className="bg-indigo-50/50 border border-indigo-200 rounded-xl p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Merge "{client.name}" into…</span>
                                <IconButton label="Close merge" size="sm" onClick={() => { setMergeOpen(false); setMergeTarget(null); }}>✕</IconButton>
                            </div>
                            {!mergeTarget ? (
                                <>
                                    <input className={inputCls} autoFocus placeholder="Search clients by name or phone…" value={mergeSearch} onChange={e => setMergeSearch(e.target.value)} />
                                    {mergeSearch.trim().length >= 2 && (
                                        <div className="max-h-40 overflow-y-auto space-y-1">
                                            {(allClients || [])
                                                .filter(c => c.id !== client.id && (
                                                    (c.name || '').toLowerCase().includes(mergeSearch.toLowerCase())
                                                    || (c.phone || '').includes(mergeSearch.replace(/\D/g, ''))
                                                ))
                                                .slice(0, 6)
                                                .map(c => (
                                                    <button key={c.id} type="button" onClick={() => setMergeTarget(c)}
                                                        className="w-full text-left px-3 py-2 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 flex items-center justify-between gap-2">
                                                        <span className="text-sm font-semibold text-slate-800">{c.name}</span>
                                                        <span className="text-[11px] text-slate-400">{formatPhone(c.phone)}</span>
                                                    </button>
                                                ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="space-y-2">
                                    <p className="text-[12px] text-slate-600 leading-relaxed">
                                        Move everything on <span className="font-bold">{client.name}</span> (estimates, jobs, photos, notes, waivers)
                                        into <span className="font-bold text-indigo-700">{mergeTarget.name}</span>, then delete this record?
                                        {' '}This can't be undone.
                                    </p>
                                    <div className="flex gap-2">
                                        <Button size="sm" className="flex-1" onClick={doMerge} loading={merging}>
                                            Merge into {mergeTarget.name}
                                        </Button>
                                        <Button variant="secondary" size="sm" onClick={() => setMergeTarget(null)} disabled={merging}>
                                            Back
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Delete client (Jaylen 2026-09-21: "no current way to
                        delete a client entry besides backend"). Same tier as
                        merge; db.deleteClient refuses while anything real
                        (booked quote, crewed / done job, storage) is attached. */}
                    {!mergeOpen && (
                        <div className="mt-2">
                            <Button variant="tertiary" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteConfirm(true)}>
                                Delete this client…
                            </Button>
                        </div>
                    )}
                </div>
            )}
            </>
            )}

            {/* Keep PricingTab mounted (hidden) so local state survives tab switches.
                It prices the CURRENT MOVE (2026-09-18): identity comes from the
                move, the new estimate is an OPTION on it (db.createOption).
                `initialFrom` = "Alternate pricing" from the Estimates tab —
                pricing fields copied from that option, same move. */}
            {isExisting && (
                <div style={{ display: activeTab === 'pricing' ? 'block' : 'none' }}>
                    <ClientPricingTab client={liveClient} form={form} set={set} config={config} trucks={modalTrucks || []} readOnly={readOnly} saveEstimate={saveEstimate} estimates={clientEstimates} initialFrom={pricingFrom} onEstimateCreated={(created) => {
                        if (created?.id) setPendingCreated(prev => prev.some(p => p.id === created.id) ? prev : [...prev, created]);
                        setActiveTab('estimates');
                        // Creating an estimate captures the form's move details
                        // into the estimate — so commit any pending CLIENT edits
                        // too, silently. Without this, exiting after Create
                        // Estimate raised the "Unsaved changes" guard for edits
                        // the operator rightly considers saved (Jensen
                        // 2026-08-25) — and Discard would silently drop the
                        // very fields the estimate was just built from.
                        if (dirty) handleSave({ stayOpen: true });
                    }} />
                </div>
            )}

            {isExisting && activeTab === 'estimates' && (
                <ClientEstimatesTab
                    estimates={clientEstimates}
                    clients={[liveClient]}
                    move={current}
                    onCloseParent={onClose}
                    onAlternatePricing={(est) => { setPricingFrom({ est: est || null, nonce: Date.now() }); setActiveTab('pricing'); }}
                    onNewMove={() => newMove('blank')}
                />
            )}

            {isExisting && activeTab === 'photos' && (
                <div className="space-y-3">
                    <PhotoUpload clientId={client.id} onUploaded={handlePhotoUploaded} />
                    <PhotoGallery photos={photos} onDelete={handlePhotoDelete} onReorder={handlePhotoReorder} />
                </div>
            )}

            {/* Unified Notes — office (internal) + crew, timestamped,
                addable inline without entering edit mode (sql/063). */}
            {isExisting && activeTab === 'notes' && (
                <ClientNotesPanel clientId={client.id} draft={noteDraft} onDraftChange={setNoteDraft} />
            )}

            {/* Activity feed — audit_log via the Postgres triggers in
                sql/030_audit_log.sql. Existing clients only. */}
            {isExisting && activeTab === 'activity' && (
                <ActivityLog tableName="clients" recordId={client.id} />
            )}


            {/* Unsaved-changes confirm (in-app, replaces window.confirm) */}
            {closeConfirm && createPortal(
                <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={() => setCloseConfirm(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="px-5 pt-5 pb-3">
                            <h3 className="font-lemonmilk text-base text-slate-900 uppercase tracking-wide">{client?.id && editing ? 'Some changes couldn’t be saved' : 'Unsaved changes'}</h3>
                            <p className="text-sm text-slate-600 mt-2">{client?.id && editing ? `${autosave.error ? autosave.error.charAt(0).toUpperCase() + autosave.error.slice(1) + '. ' : ''}Leave anyway? Those edits will be lost.` : 'Leave without saving? Your edits will be lost.'}</p>
                        </div>
                        <div className="flex items-center gap-2 px-5 py-3 bg-slate-50 border-t border-slate-200">
                            <Button variant="secondary" className="flex-1" onClick={() => { pendingSwitchRef.current = null; pendingLeaveRef.current = null; setCloseConfirm(false); }}>
                                Keep editing
                            </Button>
                            <Button variant="danger" className="flex-1" onClick={discardAndClose}>
                                Discard
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}

            <ConfirmDialog
                open={removeMoveConfirm}
                title="Remove this move?"
                confirmLabel="Remove move"
                cancelLabel="Keep it"
                danger
                onCancel={() => setRemoveMoveConfirm(false)}
                onConfirm={doRemoveMove}
            >
                Removes <span className="font-semibold text-slate-900">{current ? moveDatesSummary(current) : 'this move'}</span> from {client?.name || 'this client'}'s file. It has no quote and nothing on the dispatch board; the file goes back to their other move.
            </ConfirmDialog>

            <ConfirmDialog
                open={deleteConfirm}
                title={`Delete ${client?.name || 'this client'}?`}
                confirmLabel={deleting ? 'Deleting…' : 'Delete client'}
                cancelLabel="Keep them"
                danger
                onCancel={() => { if (!deleting) setDeleteConfirm(false); }}
                onConfirm={doDeleteClient}
            >
                Removes <span className="font-semibold text-slate-900">{client?.name || 'this client'}</span> and everything in their file — moves, quotes, photos, notes and any unassigned legs on the board. This can't be undone. A client with a booked quote, a crewed or completed job, or storage on file can't be deleted until those are cancelled.
            </ConfirmDialog>

            {/* Cancel-walkthrough confirm (in-app, replaces window.confirm — NEW-1). */}
            <ConfirmDialog
                open={cancelWtConfirm}
                title="Cancel walkthrough?"
                confirmLabel="Cancel walkthrough"
                cancelLabel="Keep it"
                onCancel={() => setCancelWtConfirm(false)}
                onConfirm={handleCancelWalkthrough}
            >
                Cancel <span className="font-semibold text-slate-900">{client?.name || 'this client'}</span>'s walkthrough and mark them inactive? They'll drop off the dashboard queues and the walkthrough is removed from dispatch.
            </ConfirmDialog>

            {/* Possible-duplicate dialog (STRONG match on create). Three ways
                out: open the existing file, create anyway, or keep editing.
                Backdrop click = keep editing (never a destructive default). */}
            {dupePrompt && createPortal((() => {
                const cands = (dupePrompt.matches && dupePrompt.matches.length ? dupePrompt.matches : [dupePrompt.match]).slice(0, 6);
                const many = cands.length > 1;
                return (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={() => setDupePrompt(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="px-5 pt-5 pb-3">
                            <h3 className="font-lemonmilk text-base text-slate-900 uppercase tracking-wide">Possible duplicate</h3>
                            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                                {many ? (
                                    <>This phone number is already on <span className="font-bold text-slate-900">{cands.length} existing clients</span>. Open one, or create a new record.</>
                                ) : (
                                    <>This looks like <span className="font-bold text-slate-900">{cands[0].name}</span>
                                    {cands[0].phone ? <span className="text-slate-500"> · {formatPhone(cands[0].phone)}</span> : ''}
                                    {' '}— {dupePrompt.reason}.</>
                                )}
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
                            {cands.map(c => (
                                <Button
                                    key={c.id}
                                    variant="secondary"
                                    full
                                    className="justify-start text-left !h-auto py-2"
                                    onClick={() => { setDupePrompt(null); onClose(); navigateToClient(c.id); }}
                                >
                                    <span className="flex flex-col min-w-0">
                                        <span className="block">Open {c.name}'s file</span>
                                        {c.origin_address && <span className="block text-xs font-normal text-slate-500 truncate">{c.origin_address}</span>}
                                    </span>
                                </Button>
                            ))}
                            <Button
                                full
                                onClick={() => { dupeOverrideRef.current = true; setDupePrompt(null); handleSave(); }}
                            >
                                Create anyway — it's a different person
                            </Button>
                            <Button
                                variant="tertiary"
                                full
                                onClick={() => setDupePrompt(null)}
                            >
                                Keep editing
                            </Button>
                        </div>
                    </div>
                </div>
                );
            })(),
                document.body,
            )}

            {quickDispatch && (
                <QuickDispatchModal
                    client={quickDispatch}
                    onClose={() => {
                        setQuickDispatch(null);
                        // From a fresh intake save, the intake's work is done
                        // whether they sent the job or hit "Not now".
                        if (quickFromIntakeRef.current) onClose();
                    }}
                    showToast={show}
                />
            )}
        </SlideOver>
    );
}
